import { ragApi } from './ApiService'
import type {
  BillingAccount,
  BillingAccountInput,
  BillingLapsedReservation,
  BillingPack,
  BillingPayerBalance,
  BillingPaymentAccount,
  BillingReport,
  BillingReportGroup,
  BillingSale,
} from '@/types/billing'

const BASE = '/api/agent/v1/billing/accounts'

function data<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
    return (response as { data: T }).data
  }
  return response as T
}

/** Billing configuration, through the rag-api client: never a hand-built header. */
export const billingService = {
  async listAccounts(): Promise<BillingAccount[]> {
    return data<{ items: BillingAccount[] }>(await ragApi.get(`${BASE}/`)).items
  },
  async createAccount(input: BillingAccountInput): Promise<{ account: BillingAccount; key: string }> {
    return data(await ragApi.post(`${BASE}/`, input))
  },
  async updateAccount(id: number, input: Partial<BillingAccountInput>): Promise<BillingAccount> {
    return data(await ragApi.patch(`${BASE}/${id}/`, input))
  },
  async issueKey(id: number, label: string): Promise<{ prefix: string; key: string }> {
    return data(await ragApi.post(`${BASE}/${id}/keys/`, { label }))
  },
  async revokeKey(id: number, prefix: string): Promise<void> {
    await ragApi.post(`${BASE}/${id}/keys/${prefix}/revoke/`)
  },
  async savePack(id: number, pack: BillingPack): Promise<BillingPack> {
    return data(await ragApi.put(`${BASE}/${id}/packs/`, pack))
  },
  async connectPayments(id: number, returnUrl: string, country: string): Promise<string> {
    return data<{ url: string }>(
      await ragApi.post(`${BASE}/${id}/connect/`, { country, return_url: returnUrl, refresh_url: returnUrl }),
    ).url
  },
  async linkExistingPayments(id: number, redirectUri: string): Promise<string> {
    return data<{ url: string }>(await ragApi.post(`${BASE}/${id}/connect/existing/`, { redirect_uri: redirectUri })).url
  },
  async finishLinkExisting(code: string, state: string): Promise<BillingPaymentAccount> {
    return data(await ragApi.post('/api/agent/v1/billing/connect/existing/finish/', { code, state }))
  },
  async listSales(id: number): Promise<BillingSale[]> {
    return data<{ items: BillingSale[] }>(await ragApi.get(`${BASE}/${id}/sales/`)).items
  },
  async listBalances(id: number): Promise<BillingPayerBalance[]> {
    return data<{ items: BillingPayerBalance[] }>(await ragApi.get(`${BASE}/${id}/balances/`)).items
  },
  async listLapsed(id: number): Promise<BillingLapsedReservation[]> {
    return data<{ items: BillingLapsedReservation[] }>(await ragApi.get(`${BASE}/${id}/reservations/lapsed/`)).items
  },
  async margin(id: number, start: string, end: string, group: BillingReportGroup): Promise<BillingReport> {
    return data(await ragApi.get(`${BASE}/${id}/margin/`, { params: { start, end, group } }))
  },
}
